import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';

const RegisterContainer = styled.div`
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.xl};
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes.xxl};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.primary};
  text-align: center;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 90px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.primary};
  }
  
  letter-spacing: 1px;
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Label = styled.label<{ active?: boolean }>`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  font-weight: 600;
  color: ${({ theme, active }) => active ? theme.colors.primary : theme.colors.text};
  position: relative;
  padding-left: ${({ theme }) => theme.spacing.sm};
  transition: all 0.3s ease;
  
  &:before {
    content: '•';
    position: absolute;
    left: 0;
    color: ${({ theme }) => theme.colors.primary};
  }
  
  &:active {
    color: ${({ theme }) => theme.colors.primary};
    transform: translateX(2px);
  }
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-family: inherit;
`;

// const ErrorMessage = styled.p`
//   color: ${({ theme }) => theme.colors.danger};
//   font-size: ${({ theme }) => theme.fontSizes.sm};
//   margin-top: ${({ theme }) => theme.spacing.xs};
//   text-align: center;
// `;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.danger};
  background-color: rgba(239, 68, 68, 0.1);
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSizes.sm};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: 4px;
  font-weight: 500;
  font-size: ${({ theme }) => theme.fontSizes.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};

  &:hover {
    background-color: ${({ theme }) => theme.colors.info};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.secondary};
    cursor: not-allowed;
  }
`;

const LoginLink = styled.div`
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.md};
  
  a {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeField, setActiveField] = useState<string | null>(null);
  
  // useAuth 훅 사용
  const { isAuthenticated, register, error: authError } = useAuth();

  useEffect(() => {
    // 이미 로그인 상태라면 홈으로 리다이렉트
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);
  
  // 인증 에러가 발생하면 컴포넌트 에러 상태에 반영
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!username.trim() || !email.trim() || !password.trim() || !confirmPassword.trim() || !nickname.trim()) {
      setError('모든 항목을 입력해주세요.');
      return;
    }

    if (password !== confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await register({
        username,
        email,
        password,
        nickname
      });
      alert('회원가입이 완료되었습니다. 로그인해주세요.');
      navigate('/login');
    } catch (error: any) {
      console.error('회원가입 오류:', error);
      if (!error.message) {
        setError('회원가입 중 오류가 발생했습니다.');
      }
      setIsSubmitting(false);
    }
  };

  const handleFocus = (fieldId: string) => {
    setActiveField(fieldId);
  };

  const handleBlur = () => {
    setActiveField(null);
  };

  return (
    <RegisterContainer>
      <PageTitle>회원가입</PageTitle>
      
      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="username" active={activeField === 'username'}>아이디</Label>
          <Input
            id="username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="아이디를 입력해주세요"
            required
            onFocus={() => handleFocus('username')}
            onBlur={handleBlur}
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="nickname" active={activeField === 'nickname'}>닉네임</Label>
          <Input
            id="nickname"
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="닉네임을 입력해주세요"
            required
            onFocus={() => handleFocus('nickname')}
            onBlur={handleBlur}
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="email" active={activeField === 'email'}>이메일</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="이메일을 입력해주세요"
            required
            onFocus={() => handleFocus('email')}
            onBlur={handleBlur}
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="password" active={activeField === 'password'}>비밀번호</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="비밀번호를 입력해주세요"
            required
            onFocus={() => handleFocus('password')}
            onBlur={handleBlur}
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="confirmPassword" active={activeField === 'confirmPassword'}>비밀번호 확인</Label>
          <Input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="비밀번호를 다시 입력해주세요"
            required
            onFocus={() => handleFocus('confirmPassword')}
            onBlur={handleBlur}
          />
        </FormGroup>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <SubmitButton type="submit" disabled={isSubmitting}>
          {isSubmitting ? '가입 중...' : '회원가입'}
        </SubmitButton>
      </form>
      
      <LoginLink>
        이미 계정이 있으신가요? <Link to="/login">로그인</Link>
      </LoginLink>
    </RegisterContainer>
  );
};

export default Register; 